import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCreateTag, useTags } from "@/hooks/use-tags";
import { api, type Tag } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Read-only tag names. */
export function TagList({ ids, tags, className }: { ids: number[]; tags?: Tag[]; className?: string }) {
  const named = ids.map((id) => tags?.find((t) => t.id === id)).filter(Boolean) as Tag[];
  if (!named.length) return null;
  return (
    <span className={cn("inline-flex flex-wrap gap-1", className)}>
      {named.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1 rounded-md bg-ai-from/15 px-1.5 py-0.5 text-[11px] text-ai-from">
          <TagIcon className="size-3" />{t.name}
        </span>
      ))}
    </span>
  );
}

/** Toggle buttons for the user's tags plus an inline "new tag" field. */
export function TagToggles({ value, onChange, disabled }: { value: number[]; onChange: (ids: number[]) => void; disabled?: boolean }) {
  const { data: tags = [] } = useTags();
  const create = useCreateTag();
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const add = async () => {
    if (!name.trim()) return setAdding(false);
    let tag: Tag;
    try {
      tag = await create.mutateAsync(name.trim());
    } catch {
      return; // useCreateTag already showed the error; the field stays for a retry
    }
    onChange([...value.filter((x) => x !== tag.id), tag.id]);
    setName("");
    setAdding(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags.map((t) => {
        const on = value.includes(t.id);
        return (
          <Button key={t.id} type="button" size="sm" variant="outline" aria-pressed={on} disabled={disabled} onClick={() => toggle(t.id)}
            className={cn(on && "border-ai-from bg-ai-from/15 text-ai-from hover:bg-ai-from/20 hover:text-ai-from")}>
            {on ? <Check className="size-4" /> : <TagIcon className="size-4" />} {t.name}
          </Button>
        );
      })}
      {adding ? (
        <form className="flex gap-2" onSubmit={(e) => (e.preventDefault(), add())}>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={32} placeholder="Праки, команда…" className="h-8 w-40" />
          <Button type="submit" size="sm" disabled={create.isPending}>OK</Button>
        </form>
      ) : (
        <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => setAdding(true)}>
          <Plus className="size-4" /> Тег
        </Button>
      )}
    </div>
  );
}

/** Button + dialog that edits the user's tags on one match. */
export function MatchTagsButton({ matchId, value }: { matchId: string; value: number[] }) {
  const qc = useQueryClient();
  const { data: tags } = useTags();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const save = useMutation({
    mutationFn: (ids: number[]) => api(`/api/matches/${matchId}/tags`, { method: "PUT", json: { tagIds: ids } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["match", String(matchId)] });
      qc.invalidateQueries({ queryKey: ["demos"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["player"] });
      setOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <>
      <button type="button" onClick={() => (setDraft(value), setOpen(true))} className="inline-flex flex-wrap items-center gap-1">
        {value.length ? <TagList ids={value} tags={tags} /> : (
          <span className="inline-flex items-center gap-1 rounded-md border border-dashed px-1.5 py-0.5 text-[11px] hover:text-foreground">
            <Plus className="size-3" /> тег
          </span>
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Теги матча</DialogTitle>
            <DialogDescription>Свои метки, например «Праки» или название команды. По ним можно смотреть статистику в профиле.</DialogDescription>
          </DialogHeader>
          <TagToggles value={draft} onChange={setDraft} />
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>Сохранить</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
